import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

type Screen = 'gate' | 'intro' | 'slider' | 'question' | 'days' | 'complete'
type Day = 'Sábado' | 'Domingo'

/* ── helpers ──────────────────────────────────────────── */

function checkPassword(raw: string): boolean {
  const n = raw.trim().toLowerCase().replace(/\s+/g, '')
  return ['sapo', 'sapocururu', 'cururu'].includes(n)
}

const stressLabel = (v: number) =>
  v <= 3 ? { text: 'BAIXO', color: '#4ade80' }
  : v <= 6 ? { text: 'MODERADO', color: '#facc15' }
  : { text: 'CRÍTICO', color: '#f87171' }

const stressMsg = (v: number) =>
  v <= 3 ? 'Trégua temporária detectada. Janela favorável.'
  : v <= 6 ? 'Nível moderado. Intervenção preventiva recomendada.'
  : 'Nível crítico. O sistema recomenda compensação imediata.'

/* ── micro components ─────────────────────────────────── */

function Blink() {
  return (
    <motion.span
      animate={{ opacity: [1, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, repeatType: 'reverse' }}
      style={{ marginLeft: 2, color: '#4ade80' }}
    >█</motion.span>
  )
}

function TypeLine({
  text, delayMs = 0, speed = 25, onDone,
}: { text: string; delayMs?: number; speed?: number; onDone?: () => void }) {
  const [shown, setShown] = useState('')
  const [go, setGo] = useState(false)
  useEffect(() => { const t = setTimeout(() => setGo(true), delayMs); return () => clearTimeout(t) }, [delayMs])
  useEffect(() => {
    if (!go) return
    let i = 0; setShown('')
    const id = setInterval(() => {
      i++; setShown(text.slice(0, i))
      if (i >= text.length) { clearInterval(id); onDone?.() }
    }, speed)
    return () => clearInterval(id)
  }, [go, text, speed])
  return <span>{shown}{shown.length < text.length && <Blink />}</span>
}

/* ── styles ──────────────────────────────────────────── */

const G: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#0d0d0b',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    fontFamily: '"Courier New", Courier, monospace',
  },
  card: {
    width: '100%',
    maxWidth: 480,
    background: '#141210',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    overflow: 'hidden',
  },
  cardHead: {
    padding: '10px 18px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardBody: { padding: '1.75rem 1.75rem 2rem' },
  tag: {
    fontSize: 'clamp(9px, 2.5vw, 10px)' as unknown as number,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    color: '#777',
    marginBottom: 6,
  },
  h1: { fontSize: 'clamp(15px, 4.5vw, 20px)' as unknown as number, fontWeight: 700, color: '#4ade80', letterSpacing: '0.04em', lineHeight: 1.3 },
  body: { fontSize: 'clamp(13px, 3.5vw, 15px)' as unknown as number, color: '#e0e0e0', lineHeight: 1.7 },
  muted: { fontSize: 'clamp(11px, 3vw, 13px)' as unknown as number, color: '#999', lineHeight: 1.6 },
  divider: { height: 1, background: 'rgba(255,255,255,0.06)', margin: '1.25rem 0' },
  input: {
    width: '100%',
    background: '#0d0d0b',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 6,
    padding: '0.65rem 0.9rem',
    color: '#e8e8e8',
    fontSize: 'clamp(13px, 3.5vw, 15px)' as unknown as number,
    fontFamily: '"Courier New", Courier, monospace',
    outline: 'none',
    boxSizing: 'border-box' as const,
    marginTop: 8,
    marginBottom: 16,
  },
}

function Btn({
  label, variant = 'primary', onClick, style,
}: {
  label: string
  variant?: 'primary' | 'ghost' | 'danger' | 'warn'
  onClick?: () => void
  style?: React.CSSProperties
}) {
  const colors: Record<string, { border: string; color: string; bg: string }> = {
    primary: { border: 'rgba(74,222,128,0.5)', color: '#4ade80', bg: 'rgba(74,222,128,0.08)' },
    ghost:   { border: 'rgba(255,255,255,0.12)', color: '#888', bg: 'transparent' },
    danger:  { border: 'rgba(248,113,113,0.5)', color: '#f87171', bg: 'rgba(248,113,113,0.06)' },
    warn:    { border: 'rgba(250,204,21,0.5)', color: '#facc15', bg: 'rgba(250,204,21,0.06)' },
  }
  const c = colors[variant]
  return (
    <button
      onClick={onClick}
      style={{
        padding: 'clamp(0.5rem, 2.5vw, 0.6rem) clamp(1rem, 4vw, 1.4rem)',
        border: `1px solid ${c.border}`,
        borderRadius: 5,
        background: c.bg,
        color: c.color,
        fontSize: 'clamp(12px, 3.2vw, 14px)',
        fontFamily: '"Courier New", Courier, monospace',
        fontWeight: 600,
        letterSpacing: '0.07em',
        cursor: 'pointer',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {label}
    </button>
  )
}

const fade = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -8 },
  transition: { duration: 0.3 },
}

/* ── main ─────────────────────────────────────────────── */

export default function Malu() {
  const [screen, setScreen] = useState<Screen>('gate')
  const [introReady, setIntroReady] = useState(false)
  const [pass, setPass] = useState('')
  const [passErr, setPassErr] = useState(false)
  const [stress, setStress] = useState(5)
  const [day, setDay] = useState<Day | null>(null)
  const [ambosTried, setAmbosTried] = useState(false)

  // áudio — Cruel Summer
  const audioRef = useRef<HTMLAudioElement>(null)
  const [muted, setMuted] = useState(false)
  useEffect(() => {
    if (screen !== 'gate' && audioRef.current) {
      audioRef.current.volume = 0.35
      audioRef.current.play().catch(() => {})
    }
  }, [screen])
  const toggleMute = () => {
    if (!audioRef.current) return
    audioRef.current.muted = !muted
    setMuted(m => !m)
  }

  // touch detection — mobile não tem hover, então o [NÃO] fugir não funciona
  const [isTouch, setIsTouch] = useState(false)
  useEffect(() => {
    setIsTouch(typeof window !== 'undefined' &&
      (window.matchMedia('(hover: none)').matches || 'ontouchstart' in window))
  }, [])

  // NÃO button (desktop: foge | mobile: vira SIM)
  const [naoShifts, setNaoShifts] = useState(0)
  const [naoOffset, setNaoOffset] = useState({ x: 0, y: 0 })
  const [naoGone, setNaoGone] = useState(false)
  const [naoFlipped, setNaoFlipped] = useState(false)
  const naoRef = useRef<HTMLDivElement>(null)

  const handleNaoEscape = () => {
    if (naoGone) return
    const next = naoShifts + 1
    if (next >= 4) { setNaoGone(true); return }
    setNaoShifts(next)
    const angle = Math.random() * Math.PI * 2
    const dist = 200 + Math.random() * 140
    setNaoOffset(prev => ({
      x: prev.x + Math.cos(angle) * dist,
      y: prev.y + Math.sin(angle) * dist,
    }))
  }

  const handlePass = () => {
    if (checkPassword(pass)) {
      setPassErr(false)
      // play direto no click (contexto de user gesture — evita autoplay block)
      if (audioRef.current) {
        audioRef.current.volume = 0.35
        audioRef.current.play().catch(() => {})
      }
      setScreen('intro')
    } else {
      setPassErr(true)
    }
  }

  const label = stressLabel(stress)

  const steps: Screen[] = ['intro', 'slider', 'question', 'days', 'complete']
  const stepIdx = steps.indexOf(screen)

  return (
    <div style={G.page}>
      {/* áudio background */}
      <audio ref={audioRef} src="/audio/cruel-summer.mp3" loop preload="auto" />
      {screen !== 'gate' && (
        <button
          onClick={toggleMute}
          title={muted ? 'Ativar som' : 'Silenciar'}
          style={{
            position: 'fixed', bottom: 18, right: 18, zIndex: 999,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
            color: '#aaa', fontSize: 16, lineHeight: 1,
          }}
        >{muted ? '🔇' : '🎵'}</button>
      )}

      <div style={G.card}>

        {/* Card header */}
        <div style={G.cardHead}>
          <span style={{ ...G.muted, color: '#4ade80' }}>● matheusedson.com</span>
          <span style={G.muted}>/malu</span>
        </div>

        {/* Progress bar — só nas telas pós-gate */}
        {stepIdx >= 0 && (
          <div style={{ height: 2, background: '#1a1a18' }}>
            <motion.div
              animate={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.4 }}
              style={{ height: '100%', background: '#4ade80' }}
            />
          </div>
        )}

        <div style={G.cardBody}>
          <AnimatePresence mode="wait">

            {/* ── GATE ── */}
            {screen === 'gate' && (
              <motion.div key="gate" {...fade}>
                <p style={G.tag}>Verificação de acesso</p>
                <h1 style={{ ...G.h1, marginBottom: 6 }}>
                  <TypeLine text="ACESSO RESTRITO" speed={40} />
                </h1>
                <p style={{ ...G.body, marginBottom: 20 }}>
                  Para continuar, responda:
                </p>

                <p style={{ ...G.body, color: '#aaa', marginBottom: 2 }}>
                  Qual o apelido do Matheus quando criança?
                </p>

                <input
                  style={{
                    ...G.input,
                    borderColor: passErr ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.1)',
                  }}
                  value={pass}
                  onChange={e => { setPass(e.target.value); setPassErr(false) }}
                  onKeyDown={e => e.key === 'Enter' && handlePass()}
                  placeholder="resposta..."
                  autoFocus
                />

                {passErr && (
                  <p style={{ ...G.muted, color: '#f87171', marginBottom: 12 }}>
                    acesso negado. tenta de novo.
                  </p>
                )}

                <Btn label="VERIFICAR →" onClick={handlePass} />
              </motion.div>
            )}

            {/* ── INTRO ── */}
            {screen === 'intro' && (
              <motion.div key="intro" {...fade}>
                <p style={G.tag}>Sistema de qualificação v2.0</p>
                <h1 style={{ ...G.h1, marginBottom: 8 }}>
                  <TypeLine text="PROTOCOLO: SEGUNDO ENCONTRO" speed={32} />
                </h1>
                <p style={{ ...G.body, color: '#666', marginBottom: 24 }}>
                  <TypeLine
                    text="Iniciando análise de viabilidade..."
                    delayMs={1100}
                    speed={20}
                    onDone={() => setTimeout(() => setIntroReady(true), 500)}
                  />
                </p>
                {introReady && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Btn label="INICIAR →" onClick={() => setScreen('slider')} />
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* ── SLIDER ── */}
            {screen === 'slider' && (
              <motion.div key="slider" {...fade}>
                <p style={G.tag}>Parâmetro 01 — stress acadêmico</p>
                <p style={{ ...G.body, fontWeight: 600, marginBottom: 4 }}>
                  A faculdade ainda tá te matando?
                </p>
                <p style={{ ...G.muted, marginBottom: 20 }}>Calibre o indicador</p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={G.muted}>1</span>
                  <input
                    type="range" min={1} max={10} value={stress}
                    onChange={e => setStress(Number(e.target.value))}
                    style={{ flex: 1, accentColor: label.color, cursor: 'pointer' }}
                  />
                  <span style={G.muted}>10</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, color: label.color }}>{stress}</span>
                  <span style={{ ...G.muted, fontSize: 11, alignSelf: 'center', color: label.color, letterSpacing: '0.12em' }}>
                    [{label.text}]
                  </span>
                </div>

                <p style={{ ...G.muted, marginBottom: 24, fontStyle: 'italic' }}>
                  {stressMsg(stress)}
                </p>

                <Btn label="CONFIRMAR →" onClick={() => setScreen('question')} />
              </motion.div>
            )}

            {/* ── QUESTION ── */}
            {screen === 'question' && (
              <motion.div key="question" {...fade}>
                <p style={G.tag}>Análise concluída</p>

                <div style={{
                  background: '#0d0d0b',
                  border: `1px solid ${label.color}22`,
                  borderRadius: 6,
                  padding: '10px 14px',
                  marginBottom: 20,
                }}>
                  <span style={{ ...G.muted, color: label.color, fontSize: 11 }}>
                    [{label.text}] {stressMsg(stress)}
                  </span>
                </div>

                <div style={G.divider} />

                <p style={{ ...G.body, color: '#999', marginBottom: 6 }}>Questão central:</p>
                <p style={{
                  fontSize: 'clamp(17px, 5vw, 22px)',
                  fontWeight: 700, color: '#4ade80',
                  letterSpacing: '0.02em', marginBottom: 28, lineHeight: 1.35,
                }}>
                  Vamos sair esse fim de semana?
                </p>

                <div style={{ display: 'flex', gap: 12, position: 'relative', minHeight: 70, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Btn label="[SIM]" onClick={() => setScreen('days')} style={{ minWidth: 96 }} />

                  {/* MOBILE: [NÃO] vira [SIM] ao toque */}
                  {isTouch && !naoFlipped && (
                    <Btn
                      label="[NÃO]"
                      variant="danger"
                      style={{ minWidth: 96 }}
                      onClick={() => setNaoFlipped(true)}
                    />
                  )}
                  {isTouch && naoFlipped && (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                    >
                      <Btn
                        label="[SIM]"
                        variant="primary"
                        style={{ minWidth: 96 }}
                        onClick={() => setScreen('days')}
                      />
                    </motion.div>
                  )}
                  {isTouch && naoFlipped && (
                    <motion.span
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      style={{ ...G.muted, fontStyle: 'italic', color: '#666', flexBasis: '100%' }}
                    >
                      corrigido pelo sistema.
                    </motion.span>
                  )}

                  {/* DESKTOP: [NÃO] foge do cursor */}
                  {!isTouch && !naoGone && (
                    <motion.div
                      ref={naoRef}
                      style={{ display: 'inline-block', position: 'relative' }}
                      animate={{ x: naoOffset.x, y: naoOffset.y }}
                      transition={{ type: 'spring', stiffness: 900, damping: 10, mass: 0.4 }}
                    >
                      <Btn label="[NÃO]" variant="danger" style={{ display: 'block', minWidth: 96 }} onClick={() => {}} />
                      <div
                        onMouseEnter={handleNaoEscape}
                        style={{ position: 'absolute', inset: -24, cursor: 'not-allowed' }}
                      />
                    </motion.div>
                  )}
                  {!isTouch && naoGone && (
                    <motion.p
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      style={{ ...G.muted, alignSelf: 'center', fontStyle: 'italic', color: '#666' }}
                    >
                      o sistema não aceita recurso.
                    </motion.p>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── DAYS ── */}
            {screen === 'days' && (
              <motion.div key="days" {...fade}>
                <p style={G.tag}>Viabilidade confirmada ✓</p>
                <p style={{ ...G.body, fontWeight: 600, marginBottom: 20 }}>
                  Preferência de dia:
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
                  <Btn
                    label="SÁBADO"
                    variant={day === 'Sábado' ? 'primary' : 'ghost'}
                    style={{ width: '100%', padding: 'clamp(0.5rem, 2.5vw, 0.6rem) 0.4rem' }}
                    onClick={() => { setDay('Sábado'); setAmbosTried(false) }}
                  />
                  <Btn
                    label="DOMINGO"
                    variant={day === 'Domingo' ? 'primary' : 'ghost'}
                    style={{ width: '100%', padding: 'clamp(0.5rem, 2.5vw, 0.6rem) 0.4rem' }}
                    onClick={() => { setDay('Domingo'); setAmbosTried(false) }}
                  />
                  <Btn
                    label="AMBOS"
                    variant={ambosTried ? 'warn' : 'ghost'}
                    style={{ width: '100%', padding: 'clamp(0.5rem, 2.5vw, 0.6rem) 0.4rem' }}
                    onClick={() => { setAmbosTried(true); setDay(null) }}
                  />
                </div>

                <AnimatePresence>
                  {ambosTried && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      style={{
                        background: 'rgba(250,204,21,0.05)',
                        border: '1px solid rgba(250,204,21,0.15)',
                        borderRadius: 6,
                        padding: '10px 14px',
                        marginBottom: 16,
                      }}
                    >
                      <p style={{ ...G.muted, color: '#facc15', fontStyle: 'italic' }}>
                        sistema analisa... acho que vc não tem tempo pra isso.
                      </p>
                      <p style={{ ...G.muted, marginTop: 4 }}>Escolha só um.</p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {day && !ambosTried && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Btn label={`CONFIRMAR ${day.toUpperCase()} →`} style={{ width: '100%' }} onClick={() => setScreen('complete')} />
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* ── COMPLETE ── */}
            {screen === 'complete' && (
              <motion.div key="complete" {...fade}>
                <p style={{ ...G.tag, color: '#4ade80' }}>● candidatura aprovada</p>

                <div style={G.divider} />

                <div style={{ marginBottom: 20 }}>
                  {[
                    'italiano fluente: parcialmente detectado',
                    'resistência ao transporte: documentada',
                    'parque do sabiá: coordenadas arquivadas',
                    'pediatria vs. outras áreas: ainda em análise',
                    `disponibilidade ${day}: CONFIRMADA`,
                  ].map((line, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.12, duration: 0.25 }}
                      style={{ ...G.body, color: '#bbb', marginBottom: 10, display: 'flex', gap: 8 }}
                    >
                      <span style={{ color: '#4ade80', flexShrink: 0 }}>✓</span>
                      <span>{line}</span>
                    </motion.p>
                  ))}
                </div>

                <div style={G.divider} />

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <p style={{ ...G.body, marginBottom: 16 }}>
                    Confirmado para <strong style={{ color: '#4ade80' }}>{day}</strong>.
                    Agora é só o horário.
                  </p>

                  <Btn
                    label="CONFIRMAR NO WHATSAPP →"
                    style={{ width: '100%', marginBottom: 16 }}
                    onClick={() => {
                      window.location.href = 'https://wa.me/?text=' + encodeURIComponent(`passei no protocolo. ${day?.toLowerCase()} tá confirmado, que horas`)
                    }}
                  />

                  <p style={{ ...G.muted, fontStyle: 'italic', lineHeight: 1.7 }}>
                    P.S.: o sistema não precisava de um link pra te convidar.<br />
                    Mas foi mais divertido assim.
                  </p>
                </motion.div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
